import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AllEmployeeGroupsDocument,
  BulkAssignEmployeesToGroupDocument,
  EmployeeGroupAssignModalDataDocument,
  EmployeeGroupRootPageDocument,
  SettingsEmployeeGroupFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

interface Props {
  employeeGroup: SettingsEmployeeGroupFragment
  onClose: () => void
}

const AssignModal = ({ onClose, employeeGroup }: Props) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [bulkAssignEmployeesToGroup] = useMutation(BulkAssignEmployeesToGroupDocument, {
    extractorFn: (data) => data.employeesMutations.bulkAssignToEmployeeGroupEmployeesEmployee,
  })

  const existingEmployeeIds = employeeGroup.employees
    .map((employee) => employee.employeeName?.id)
    .filter((id): id is string => id !== undefined)

  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: existingEmployeeIds,
  })

  const bulkUpdateEmployeesEmployeeGroup = (
    selection: EmployeeSelectorV2MultiSelectState,
    employeeGroupId: string,
    legalEntityId: string
  ) => {
    return bulkAssignEmployeesToGroup({
      variables: {
        employeeSelection: selection,
        employeeGroupId: employeeGroupId,
        legalEntityId: legalEntityId,
      },
      refetchQueries: [
        AllEmployeeGroupsDocument,
        EmployeeGroupRootPageDocument,
        EmployeeGroupAssignModalDataDocument,
      ],
    })
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    setLoading(true)
    const result = await bulkUpdateEmployeesEmployeeGroup(
      employeeSelection,
      employeeGroup.id,
      employeeGroup.legalEntityId
    )
    const handled = handleResult(result, () => {
      // Structured errors are handled by handleResult automatically
    })

    if (!handled.success) {
      const messages = compact(
        handled.errors.flatMap((error) => {
          if (error.__typename === 'SimpleError') {
            return error.message
          } else if (error.__typename === 'StructuredError') {
            return error.messages
          }
          return []
        })
      )

      if (messages.length > 0) {
        app.setFlash({
          type: 'wrong',
          message: messages.join(', '),
        })
      }
    } else {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }

    closePortal()
    setLoading(false)
  }

  return (
    <Modal
      illustration="add-user"
      title={i18n.t('settings.legal_entities.legal_entity.employee_group.assign.title', {
        name: employeeGroup.name,
      })}
      description={i18n.t('settings.legal_entities.legal_entity.employee_group.assign.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={loading} submit>
          {i18n.t('settings.legal_entities.legal_entity.employee_group.assign.submit')}
        </Button>
      }
    >
      <EmployeeSelectorV2
        multiple
        value={existingEmployeeIds}
        onChange={setEmployeeSelection}
        options={{ asList: true }}
      />
    </Modal>
  )
}

export default AssignModal
